import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import {
  MDSTooltip,
  MDSTooltipPlacement,
  MDSPanelHeaderSlotName,
  MDSText,
  MDSPanelHeader,
  MDSTextVariant,
  MDSLensGroup,
  MDSIconButton,
  MDSPanelHeaderButtonGroup,
  MDSTextFieldSlotName,
  MDSIconButtonSize,
  MDSIcon,
  MDSIconKind,
  MDSFadeHorizontalScroll,
} from "@/mds";
import { PageBannerSection } from "@/components/banners/page-banner-section";
import { LensKind, SortByKind } from "@/modules/lenses/types";
import { useCallback } from "react";
import { NotesListFilter } from "@/modules/filters/notes/NotesListFilter";
import { CollectionsFacetFilter } from "@/components/filters/collections-facet-filter";
import { ContactsFacetFilter } from "@/components/filters/contacts-facet-filter";
import { FilterField } from "@/components/filters/filter-field";
import { ClearFiltersModal, useClearFiltersModal } from "@/components/filters/clear-filters-modal";
import { SortByDropdownButton } from "@/components/filters/sort-by-dropdown-button";

export const TemplatesListPageHeader = observer(() => {
  const page = useTabPageStore("templatesListPage");

  const onTemplatesFilterChange = useCallback(
    (value: string) => {
      page.setTemplatesFilterQuery({ query: value });
    },
    [page]
  );

  const clearTemplatesFilterQuery = useCallback(() => {
    page.setTemplatesFilterQuery({ query: "" });
  }, [page]);

  const tabs = [
    {
      label: "All",
      onClick: () =>
        page.filters.setParams({
          lens: LensKind.All,
          sortBy: SortByKind.LastModified,
        }),
      isActive: page.filters.lens === LensKind.All,
    },
    {
      label: "Created by me",
      onClick: () =>
        page.filters.setParams({
          lens: LensKind.AddedByMe,
          sortBy: SortByKind.LastModified,
        }),
      isActive: page.filters.lens === LensKind.AddedByMe,
    },
    {
      label: "Shared with me",
      onClick: () =>
        page.filters.setParams({
          lens: LensKind.SharedWithMe,
          sortBy: SortByKind.LastModified,
        }),
      isActive: page.filters.lens === LensKind.SharedWithMe,
    },
  ];

  const {
    filterButtonRef,
    showFilters,
    showFiltersModal,
    setShowFiltersModal,
    handleToggleFilters,
    handleClearFilters,
  } = useClearFiltersModal({
    clearAllFilters: page.clearAllFilters,
    activeFiltersCount: page.activeFiltersCount,
  });

  return (
    <>
      <Wrapper>
        <MDSPanelHeader>
          <MDSText
            slot={MDSPanelHeaderSlotName.Heading}
            variant={MDSTextVariant.HeaderXl}
            numberOfLines={1}
          >
            Templates
          </MDSText>
          <MDSLensGroup slot={MDSPanelHeaderSlotName.Subsection} items={tabs} />
          <MDSPanelHeaderButtonGroup slot={MDSPanelHeaderSlotName.Actions}>
            <MDSTooltip config={{ label: "Create template", placement: MDSTooltipPlacement.Top }}>
              <MDSIconButton
                iconKind={MDSIconKind.Plus}
                onClick={page.handleCreateNewTemplate}
                size={MDSIconButtonSize.Medium}
              />
            </MDSTooltip>
            <MDSTooltip config={{ label: "Show filters", placement: MDSTooltipPlacement.Top }}>
              <MDSIconButton
                iconKind={MDSIconKind.Filter}
                onClick={handleToggleFilters}
                ref={filterButtonRef}
                isToggled={showFilters}
                size={MDSIconButtonSize.Medium}
              />
            </MDSTooltip>
          </MDSPanelHeaderButtonGroup>
        </MDSPanelHeader>

        {showFilters && (
          <WrapperBottom>
            <FiltersContainer>
              <FilterField
                placeholder="Filter by name"
                value={page.templatesFilterQuery}
                onChange={onTemplatesFilterChange}
                onClearText={clearTemplatesFilterQuery}
                showClearTextButton={true}
                autoFocus={true}
              >
                <MDSIcon kind={MDSIconKind.Search} slot={MDSTextFieldSlotName.LeftAddon} />
              </FilterField>
              <SortByDropdownButton
                sortOptions={page.filters.sortOptions}
                defaultSortOption={SortByKind.LastModified}
                selectedSortOption={page.filters.sortBy}
                onSelectSortOption={page.filters.setSortBy}
              />
              {renderSortedFilters(page.filters)}
            </FiltersContainer>
          </WrapperBottom>
        )}
        <PageBannerSection />
      </Wrapper>
      <ClearFiltersModal
        triggerRef={filterButtonRef}
        isOpen={showFiltersModal}
        count={page.activeFiltersCount}
        onCancel={() => setShowFiltersModal(false)}
        onConfirm={handleClearFilters}
      />
    </>
  );
});

const renderSortedFilters = (pageFilters: NotesListFilter) => {
  const filters = [
    {
      isActive:
        pageFilters.selectedCollectionTitles.length > 0 ||
        pageFilters.collectionNoCollectionIsSelected,
      defaultOrder: 0,
      component: (
        <CollectionsFacetFilter
          key="collections"
          supportNoCollectionOption
          searchText={pageFilters.collectionsSearchQuery}
          items={pageFilters.collectionsFilterItems}
          onSearchTextChange={pageFilters.setCollectionSearchQuery}
          onSearchTextClear={pageFilters.clearCollectionSearchQuery}
          onSelectItem={pageFilters.toggleCollectionItem}
          onSelectClear={pageFilters.clearCollections}
          buttonPrefix={pageFilters.collectionNoCollectionIsSelected ? "" : "In"}
          buttonLabel="Collections"
          selectedModeKey={pageFilters.selectedCollectionModeKey}
          onSelectMode={pageFilters.onCollectionModeSelectionChange}
          noCollectionIsSelected={pageFilters.collectionNoCollectionIsSelected}
          onNoCollectionIsSelected={pageFilters.setCollectionNoCollectionIsSelected}
          selectedItems={pageFilters.selectedCollectionTitles}
        />
      ),
    },
    {
      isActive: pageFilters.modifiedBySearchItems.some(item => item.isSelected),
      defaultOrder: 3,
      component: (
        <ContactsFacetFilter
          key="modifiedBy"
          items={pageFilters.modifiedBySearchItems}
          searchText={pageFilters.modifiedBySearchQuery}
          onSearchTextChange={pageFilters.setModifiedBySearchQuery}
          onSearchTextClear={pageFilters.clearModifiedBySearchQuery}
          onSelectItem={pageFilters.toggleModifiedByItem}
          onSelectClear={pageFilters.clearModifiedBy}
          buttonPrefix="Edited by"
        />
      ),
    },
    {
      isActive: pageFilters.createdBySearchItems.some(item => item.isSelected),
      defaultOrder: 4,
      component: (
        <ContactsFacetFilter
          key="createdBy"
          items={pageFilters.createdBySearchItems}
          searchText={pageFilters.createdBySearchQuery}
          onSearchTextChange={pageFilters.setCreatedBySearchQuery}
          onSearchTextClear={pageFilters.clearCreatedBySearchQuery}
          onSelectItem={pageFilters.toggleCreatedByItem}
          onSelectClear={pageFilters.clearCreatedBy}
          buttonPrefix="Created by"
        />
      ),
    },
  ];

  return filters
    .sort((a, b) => {
      const activeSort = Number(b.isActive) - Number(a.isActive);
      if (activeSort !== 0) {
        return activeSort;
      }

      return a.defaultOrder - b.defaultOrder;
    })
    .map(filter => filter.component);
};

const Wrapper = styled.div({});

const WrapperBottom = styled.div(({ theme }) => ({
  padding: theme.spacing["4"],
}));

const FiltersContainer = styled(MDSFadeHorizontalScroll)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing["2"],
}));
